import type {
  ProjectWire as AdminProject,
  ApiWire as Api,
  ApiCreatedResponse,
  ApisResponse,
  ApplicationWire as Application,
  ApplicationCreatedResponse,
  ApplicationsResponse,
  AssignRoleRequest,
  AssignRoleResponse,
  AuditEventWire as AuditEvent,
  AuditResponse,
  BrandingResponse as Branding,
  BulkResponse,
  BulkRowResult as BulkResult,
  BulkRowRequest as BulkRow,
  ClaimNameResponse,
  ClaimsResponse,
  ContextClaimWire as ContextClaim,
  EmailMessageWire as EmailMessage,
  EmailTemplatePreviewResponse,
  EmailTemplatesResponse,
  HeldRoleWire,
  HoldingsResponse,
  InvitationResponse as InvitationDetail,
  MemberWire as Member,
  MembersResponse,
  MyApplicationWire as MyApplication,
  MyApplicationsResponse,
  MyAssignmentsResponse,
  MyProjectsResponse,
  MyTenantsResponse,
  OffboardResponse,
  OkResponse,
  OrphanSubjectWire as OrphanSubject,
  OrphansResponse,
  PermissionWire as Permission,
  PermissionCreatedResponse,
  PermissionsResponse,
  PlatformTenantWire as PlatformTenant,
  PlatformTenantsResponse,
  ProjectIdResponse,
  ProjectsResponse,
  ResendInvitationResponse,
  RoleWire as Role,
  RoleAssignmentWire as RoleAssignment,
  RoleAssignmentRequest,
  RoleKeyResponse,
  RolesResponse,
  TenantWire as Tenant,
} from "@flexauth/api";

/** A project the subject can obtain a data-plane token for. */
export type Project = MyProjectsResponse["projects"][number];

export type {
  AdminProject,
  Api,
  Application,
  AuditEvent,
  BulkResponse,
  BulkRow,
  BulkResult,
  Branding,
  ContextClaim,
  EmailMessage,
  InvitationDetail,
  Member,
  MyApplication,
  OrphanSubject,
  Permission,
  PlatformTenant,
  Role,
  RoleAssignment,
  RoleAssignmentRequest,
  Tenant,
};

// ---
// Auth server client
//
// Every call carries the session cookie (`credentials: "include"`), which is why the server
// allow-lists this exact origin rather than using `*`.
//
// Access tokens are held in React state only — never localStorage. They live 15 minutes and a
// stored bearer token is a credential at rest for no benefit. Production would put them behind a
// BFF so they never reach the browser at all; this is the development shape.
// ---

const BASE = (import.meta.env.VITE_AUTH_URL as string | undefined) ?? "http://localhost:3000";

export const AUTH_BASE = BASE;

/** The reserved tenant whose members hold platform authority. */
export const PLATFORM_TENANT = "platform";

/** The signed-in user. */
export interface SessionUser {
  id: string;
  email: string;
  name?: string;
  username?: string;
}

/** A minted access token and what the server said about it. */
export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

/** A failure carrying the OAuth error code, so the UI can show it verbatim. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body: unknown = text === "" ? null : JSON.parse(text);
  if (!response.ok) {
    const record = (body ?? {}) as Record<string, unknown>;
    const code = typeof record.error === "string" ? record.error : "request_failed";
    const detail =
      typeof record.error_description === "string"
        ? record.error_description
        : typeof record.message === "string"
          ? record.message
          : response.statusText;
    throw new ApiError(response.status, code, detail);
  }
  return body as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).then(parse<T>);
}

/** Signs in with either a username or an email address. */
export function signIn(identifier: string, password: string): Promise<{ user: SessionUser }> {
  // Better Auth has a separate route per identifier type, so pick by shape.
  return identifier.includes("@")
    ? post("/api/auth/sign-in/email", { email: identifier, password })
    : post("/api/auth/sign-in/username", { username: identifier, password });
}

/**
 * Asks for a password reset link. The server answers the same whether or not the address exists,
 * so the UI must not pretend to know either.
 */
export function requestPasswordReset(email: string): Promise<{ status: boolean }> {
  return post("/api/auth/request-password-reset", {
    email,
    redirectTo: `${window.location.origin}/reset-password`,
  });
}

/** Sets a new password with the single-use token from a reset link. */
export function resetPassword(token: string, newPassword: string): Promise<{ status: boolean }> {
  return post("/api/auth/reset-password", { newPassword, token });
}

/** Changes the signed-in user's password; other sessions are ended. */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ user: SessionUser }> {
  return post("/api/auth/change-password", {
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  });
}

/** Ends the session. */
export function signOut(): Promise<unknown> {
  return post("/api/auth/sign-out", {});
}

/** Reads the current session, or null when there is none. */
export async function getSession(): Promise<{ user: SessionUser } | null> {
  const response = await fetch(`${BASE}/api/auth/get-session`, { credentials: "include" });
  if (!response.ok) return null;
  const text = await response.text();
  if (text === "" || text === "null") return null;
  return JSON.parse(text) as { user: SessionUser };
}

/** Tenants the subject belongs to. Authenticated by the session. */
export async function getTenants(): Promise<Tenant[]> {
  const response = await fetch(`${BASE}/me/tenants`, { credentials: "include" });
  const body = await parse<MyTenantsResponse>(response);
  // Copied: the contract marks a response readonly, and what the console stores is its own.
  return [...body.tenants];
}

/** The customer applications the signed-in subject can use. Needs only the session. */
export async function getMyApplications(): Promise<MyApplication[]> {
  const response = await fetch(`${BASE}/me/applications`, { credentials: "include" });
  const body = await parse<MyApplicationsResponse>(response);
  return [...body.applications];
}

/** Every role the signed-in subject holds, across tenants. Needs only the session. */
export async function getMyAssignments(): Promise<HeldRoleWire[]> {
  const response = await fetch(`${BASE}/me/assignments`, { credentials: "include" });
  const body = await parse<MyAssignmentsResponse>(response);
  return [...body.assignments];
}

/** Projects the subject can select, authenticated by a control-plane token. */
export async function getProjects(tenant: string, controlToken: string): Promise<Project[]> {
  const response = await fetch(`${BASE}/me/projects?tenant=${encodeURIComponent(tenant)}`, {
    credentials: "include",
    headers: { authorization: `Bearer ${controlToken}` },
  });
  const body = await parse<MyProjectsResponse>(response);
  return [...body.projects];
}

/** Exchanges the session for an audience-scoped access token (RFC 8693 shape). */
export async function requestToken(input: {
  resource: string;
  tenantId: string;
  projectId?: string;
}): Promise<TokenResponse> {
  const form = new URLSearchParams({
    grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
    subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
    resource: input.resource,
    tenant_id: input.tenantId,
    client_id: "flexauth-console",
  });
  if (input.projectId !== undefined) form.set("project_id", input.projectId);

  const response = await fetch(`${BASE}/oauth/token`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: form.toString(),
  });
  return parse<TokenResponse>(response);
}

/** Authorization-server metadata (RFC 8414). */
export function getMetadata(): Promise<Record<string, unknown>> {
  return fetch(`${BASE}/.well-known/oauth-authorization-server`).then(
    parse<Record<string, unknown>>,
  );
}

/** The two audiences the console must know before it holds any token. */
export interface Bootstrap {
  controlPlane: string;
  platform: string;
}

/**
 * Reads them from the server rather than carrying them as constants.
 *
 * Everything else the console offers is catalogue data for the selected tenant, fetched once a
 * control-plane token exists. These two are the exception, because minting that token means
 * naming the audience first — so the server, which is configured with them, publishes them.
 */
export async function getBootstrapAudiences(): Promise<Bootstrap> {
  const metadata = await getMetadata();
  const control = metadata["https://aiui.run/metadata/control_plane_audience"];
  const platform = metadata["https://aiui.run/metadata/platform_audience"];
  if (typeof control !== "string" || typeof platform !== "string") {
    throw new ApiError(500, "metadata_incomplete", "The server published no audiences to use");
  }
  return { controlPlane: control, platform };
}

/** Decodes a JWT without verifying it. Display only — the server and resource servers verify. */
export function decodeToken(token: string): { header: unknown; claims: Record<string, unknown> } {
  const [header, payload] = token.split(".");
  const decode = (part: string): unknown =>
    JSON.parse(
      new TextDecoder().decode(
        Uint8Array.from(atob(part.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0)),
      ),
    );
  return {
    header: decode(header ?? ""),
    claims: decode(payload ?? "") as Record<string, unknown>,
  };
}

// ---
// Administration
//
// Every call carries a control-plane access token and an explicit tenant, matching the server's
// rule that a control-plane operation names its target rather than having it inferred.
// ---

function adminGet<T>(path: string, tenant: string, token: string): Promise<T> {
  return fetch(`${BASE}/admin/${path}?tenant=${encodeURIComponent(tenant)}`, {
    credentials: "include",
    headers: { authorization: `Bearer ${token}` },
  }).then(parse<T>);
}

function adminPost<T>(path: string, tenant: string, token: string, body: unknown): Promise<T> {
  return fetch(`${BASE}/admin/${path}?tenant=${encodeURIComponent(tenant)}`, {
    method: "POST",
    credentials: "include",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(body),
  }).then(parse<T>);
}

export const admin = {
  members: (tenant: string, token: string) =>
    adminGet<MembersResponse>("members", tenant, token).then((r) => [...r.members]),
  roles: (tenant: string, token: string) => adminGet<RolesResponse>("roles", tenant, token),
  permissions: (tenant: string, token: string) =>
    adminGet<PermissionsResponse>("permissions", tenant, token).then((r) => [...r.permissions]),
  projects: (tenant: string, token: string) =>
    adminGet<ProjectsResponse>("projects", tenant, token).then((r) => [...r.projects]),
  audit: (tenant: string, token: string) =>
    adminGet<AuditResponse>("audit", tenant, token).then((r) => [...r.events]),

  createProject: (tenant: string, token: string, input: { id: string; name: string }) =>
    adminPost<ProjectIdResponse>("projects", tenant, token, input),
  setProjectStatus: (tenant: string, token: string, id: string, status: string) =>
    adminPost<OkResponse>("projects/status", tenant, token, { id, status }),
  /** Attaches a role to a subject, optionally narrowed to one project. */
  assign: (tenant: string, token: string, input: AssignRoleRequest) =>
    adminPost<AssignRoleResponse>("assignments", tenant, token, input),
  revoke: (tenant: string, token: string, id: string) =>
    adminPost<OkResponse>("assignments/revoke", tenant, token, { id }),
  /**
   * Removes everything a subject holds in this tenant, identity untouched.
   *
   * Not the same act as revoking each role in turn: it also deletes any invitation not yet
   * accepted, which would otherwise let the person reappear with the roles just taken away.
   */
  offboard: (tenant: string, token: string, subjectId: string) =>
    adminPost<OffboardResponse>("members/offboard", tenant, token, { subjectId }),
  /**
   * Issues a fresh invitation to someone who has not accepted theirs.
   *
   * The previous link stops working: the server refreshes the one pending row rather than adding
   * a second, so there is never more than one live way in per person.
   */
  resendInvitation: (tenant: string, token: string, subjectId: string) =>
    adminPost<ResendInvitationResponse>("invitations/resend", tenant, token, { subjectId }),
  createRole: (
    tenant: string,
    token: string,
    input: { key: string; grants: string[]; audiences?: string[] },
  ) => adminPost<RoleKeyResponse>("roles", tenant, token, input),
};

// ---
// Platform plane and bulk provisioning
// ---

export const platform = {
  tenants: (token: string) =>
    fetch(`${BASE}/platform/tenants`, {
      credentials: "include",
      headers: { authorization: `Bearer ${token}` },
    })
      .then(parse<PlatformTenantsResponse>)
      .then((r) => r.tenants),

  createTenant: (token: string, input: { id: string; name: string }) =>
    fetch(`${BASE}/platform/tenants`, {
      method: "POST",
      credentials: "include",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify(input),
    }).then(parse<ProjectIdResponse>),

  setTenantStatus: (token: string, id: string, status: string) =>
    fetch(`${BASE}/platform/tenants/status`, {
      method: "POST",
      credentials: "include",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ id, status }),
    }).then(parse<OkResponse>),

  appoint: (token: string, input: { tenantId: string; subjectId: string; roleKey: string }) =>
    fetch(`${BASE}/platform/appoint`, {
      method: "POST",
      credentials: "include",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify(input),
    }).then(parse<ProjectIdResponse>),

  /** Identities no tenant holds. The only listing in which they appear at all. */
  orphanedSubjects: (token: string) =>
    fetch(`${BASE}/platform/subjects/orphaned`, {
      credentials: "include",
      headers: { authorization: `Bearer ${token}` },
    })
      .then(parse<OrphansResponse>)
      .then((r) => r.subjects),

  /**
   * Resolves an account by subject id, username or email, and says which tenants still hold it.
   *
   * `found` is the part that matters: a name matching no account holds nothing either, so
   * without it the answer "no tenant holds this" is indistinguishable from a typo.
   */
  subjectHoldings: (token: string, who: string) =>
    fetch(`${BASE}/platform/subjects/holdings?subject=${encodeURIComponent(who)}`, {
      credentials: "include",
      headers: { authorization: `Bearer ${token}` },
    }).then(parse<HoldingsResponse>),

  /** Erases an identity. Refused while any tenant holds an assignment. */
  deleteSubject: (token: string, subjectId: string) =>
    fetch(`${BASE}/platform/subjects/delete`, {
      method: "POST",
      credentials: "include",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ subjectId }),
    }).then(parse<OkResponse>),
};

/**
 * The tenant's email templates.
 *
 * `list` answers every message with the text in force — the tenant's or the default — so the
 * editor opens on what people are actually receiving rather than on an empty box.
 */
export const emailTemplates = {
  list: (tenant: string, token: string) =>
    adminGet<EmailTemplatesResponse>("email-templates", tenant, token).then(
      (answer) => answer.messages,
    ),
  save: (
    tenant: string,
    token: string,
    input: { message: string; subject: string; body: string },
  ) => adminPost<OkResponse>("email-templates", tenant, token, input),
  revert: (tenant: string, token: string, message: string) =>
    adminPost<OkResponse>("email-templates/revert", tenant, token, { message }),
  preview: (
    tenant: string,
    token: string,
    input: { message: string; subject: string; body: string },
  ) => adminPost<EmailTemplatePreviewResponse>("email-templates/preview", tenant, token, input),
};

/**
 * How this tenant's pre-session pages look.
 *
 * `read` is the public endpoint, used by the invitation and reset pages before any session
 * exists; it takes no token because there is none to take.
 */
export const branding = {
  read: (tenant: string) =>
    fetch(`${BASE}/branding?tenant=${encodeURIComponent(tenant)}`).then(parse<Branding>),
  get: (tenant: string, token: string) => adminGet<Branding>("branding", tenant, token),
  save: (
    tenant: string,
    token: string,
    input: {
      displayName: string | null;
      logoUrl: string | null;
      accent: string | null;
      supportEmail: string | null;
    },
  ) => adminPost<OkResponse>("branding", tenant, token, input),
  revert: (tenant: string, token: string) =>
    adminPost<OkResponse>("branding/revert", tenant, token, {}),
};

/** Runs a bulk provisioning job. `dryRun` validates without writing. */
export function bulkUsers(
  tenant: string,
  token: string,
  users: readonly BulkRow[],
  dryRun: boolean,
  /** Where this job's invitations land, and where they hand off afterwards. */
  destination?: { landing?: string; continueTo?: string },
): Promise<BulkResponse> {
  return fetch(`${BASE}/admin/users/bulk?tenant=${encodeURIComponent(tenant)}`, {
    method: "POST",
    credentials: "include",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ dryRun, users, ...destination }),
  }).then(parse<BulkResponse>);
}

// ---
// Context claims and invitations
// ---

export const claims = {
  list: (tenant: string, token: string) =>
    adminGet<ClaimsResponse>("claims", tenant, token).then((r) => [...r.claims]),
  upsert: (tenant: string, token: string, input: ContextClaim) =>
    adminPost<ClaimNameResponse>("claims", tenant, token, input),
  remove: (tenant: string, token: string, name: string) =>
    adminPost<OkResponse>("claims/delete", tenant, token, { name }),
};

/** Looks up an invitation by token. */
export function lookupInvitation(token: string): Promise<InvitationDetail> {
  return fetch(`${BASE}/invitation?token=${encodeURIComponent(token)}`).then(
    parse<InvitationDetail>,
  );
}

/** Redeems an invitation by setting a password. */
export function acceptInvitation(token: string, password: string): Promise<InvitationDetail> {
  return fetch(`${BASE}/invitation/accept`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, password }),
  }).then(parse<InvitationDetail>);
}

export const applications = {
  list: (tenant: string, token: string) =>
    adminGet<ApplicationsResponse>("applications", tenant, token).then((r) => [...r.applications]),
  create: (
    tenant: string,
    token: string,
    input: {
      clientId: string;
      name: string;
      type: string;
      allowedAudiences: string[];
      redirectUris?: string[];
    },
  ) => adminPost<ApplicationCreatedResponse>("applications", tenant, token, input),
  /** Replaces the whole set of return URLs. */
  setRedirectUris: (tenant: string, token: string, clientId: string, redirectUris: string[]) =>
    adminPost<OkResponse>("applications/redirect-uris", tenant, token, { clientId, redirectUris }),
  setAudiences: (tenant: string, token: string, clientId: string, allowedAudiences: string[]) =>
    adminPost<OkResponse>("applications/audiences", tenant, token, {
      clientId,
      allowedAudiences,
    }),
  setStatus: (tenant: string, token: string, clientId: string, status: string) =>
    adminPost<OkResponse>("applications/status", tenant, token, { clientId, status }),
};

export const apis = {
  list: (tenant: string, token: string) =>
    adminGet<ApisResponse>("apis", tenant, token).then((r) => [...r.apis]),
  create: (tenant: string, token: string, input: { resource: string; profile: string }) =>
    adminPost<ApiCreatedResponse>("apis", tenant, token, input),
  createPermission: (
    tenant: string,
    token: string,
    input: { audience: string; key: string; description: string; machineOnly: boolean },
  ) => adminPost<PermissionCreatedResponse>("permissions", tenant, token, input),
};
